import { TrackedInputRadio } from '@objectiv/tracker-react';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';
import { forwardRef, useRef, useState } from 'react';

import { textMdSemibold } from '../../typography';
import { CardDescription } from '../card-components';

import * as styles from './RadioCardGroup.module.css';
import { ProxyInput } from './components/ProxyInput';
import { RadioOptionButton } from './components/RadioOptionButton';

import type { RadioOptionButtonProps } from './components/RadioOptionButton';
import type { UseFormCompatibleProps } from './types';
import type { RadioProps } from '../Radio';
import type { ComponentType } from 'react';

export type RadioOption<ValueType> = {
  id: string;
  label: React.ReactNode;
  value: ValueType;
  hint?: string;
  /* deprecated */
  title?: string;
  /* deprecated */
  after?: React.ReactNode | ((isChecked: boolean) => React.ReactNode);
  disabled?: boolean;
};

export type InputRadioGroupOptionProps<ValueType> = RadioOption<ValueType> & {
  checked: boolean;
  error?: string;
};

export type RadioCardGroupProps<ValueType> = {
  className?: string;
  children?: React.ReactNode;
  direction?: 'column' | 'row';
  name: string;
  value?: ValueType;
  error?: string;
  options: RadioOption<ValueType>[];
  OptionComponent?: React.FC<RadioOptionButtonProps<ValueType>>;
  onChange?: (_: ValueType) => void;
  optionToTrackingId?: (option: RadioOption<ValueType>) => string;
  Input?: ComponentType<Omit<RadioProps, 'value'> & { value: string | number }>;
};

/**
 * @deprecated use `NormalizedRadioCardGroup` instead
 */
export const RadioCardGroup = <ValueType,>({
  className,
  children,
  direction = 'row',
  name,
  value,
  error,
  options,
  OptionComponent = RadioOptionButton,
  onChange,
  Input,
  optionToTrackingId = (option) => option.id,
}: RadioCardGroupProps<ValueType>) => {
  const SafeInput =
    Input === undefined
      ? ({ trackingId, ...props }: Omit<RadioProps, 'value'> & { value: string | number }) => (
          <TrackedInputRadio {...props} objectiv={{ id: trackingId }} />
        )
      : Input;

  return (
    <div className={clsx(styles.wrapper, className)}>
      {children &&
        (typeof children === 'string' ? (
          <label className={clsx(styles.groupLabel, textMdSemibold)}>{children}</label>
        ) : (
          children
        ))}
      <div className={clsx(styles.options, styles[direction])}>
        {options?.map((option) => (
          <label key={option.id} htmlFor={option.id} className={styles.optionLabel}>
            <OptionComponent
              input={
                <SafeInput
                  id={option.id}
                  name={name}
                  checked={isEqual(value, option.value)}
                  value={option.id}
                  onChange={() => onChange?.(option.value)}
                  onClick={() => isEqual(value, option.value) && onChange?.(option.value)}
                  disabled={option.disabled ?? false}
                  trackingId={optionToTrackingId(option)}
                />
              }
              {...option}
              checked={isEqual(value, option.value)}
              error={error}
              className={styles.optionComponent}
            />
          </label>
        ))}
      </div>
    </div>
  );
};

export const NormalizedRadioCardGroup = forwardRef(
  (
    {
      className,
      children,
      direction = 'row',
      name,
      error,
      options,
      OptionComponent = RadioOptionButton,
      onChange,
      onBlur,
      Input,
      optionToTrackingId = (option) => option.id,
    }: UseFormCompatibleProps & Omit<RadioCardGroupProps<string | number>, 'onChange' | 'value'>,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const SafeInput =
      Input === undefined
        ? ({ trackingId, ...props }: Omit<RadioProps, 'value'> & { value: string | number }) => (
            <TrackedInputRadio {...props} objectiv={{ id: trackingId }} />
          )
        : Input;

    const [value, setValue] = useState<string | number>();

    const valueRef = useRef<string | number>();

    return (
      <div className={clsx(styles.wrapper, className)}>
        {children &&
          (typeof children === 'string' ? (
            <label className={clsx(styles.groupLabel, textMdSemibold)}>{children}</label>
          ) : (
            children
          ))}
        <div className={clsx(styles.options, styles[direction])}>
          {options?.map((option) => (
            <label key={option.id} htmlFor={option.id} className={styles.optionLabel}>
              <OptionComponent
                input={
                  <SafeInput
                    id={option.id}
                    name={name}
                    checked={isEqual(value, option.value)}
                    value={option.value}
                    onChange={(...args) => {
                      valueRef.current = option.value;
                      setValue(option.value);
                      onChange?.(...args);
                    }}
                    onBlur={onBlur}
                    disabled={option.disabled ?? false}
                    trackingId={optionToTrackingId(option)}
                  />
                }
                {...option}
                checked={isEqual(valueRef.current, option.value)}
                error={error}
                className={styles.optionComponent}
              />
            </label>
          ))}
          <ProxyInput<string | number>
            // We use ProxyInput to track the value change and supply `checked` prop to the `OptionalComponent`
            // Alternative way is to use sibling selector of `input[type='radio']:checked + .OptionalComponet`,
            // but it would be the leak of implementation details of this component (OptionComponent should not know if the input would be the sibling of itself)
            // The benefit of alternative way would be 1. simpler implementation of this component 2. no rerender when value changes
            // For now we're using ProxyInput, but we can reconsider when situation changes or we value things differently
            value={valueRef}
            onValueChange={(_value) => {
              if (_value !== value) {
                setValue(_value);
              }
            }}
            ref={ref as React.RefCallback<HTMLInputElement>}
          />
        </div>
      </div>
    );
  },
);

RadioCardGroup.Heading = ({ className, children }: { className?: string; children?: React.ReactNode }) => (
  <h4 className={clsx(styles.heading, textMdSemibold, className)}>{children}</h4>
);

RadioCardGroup.Description = CardDescription;

RadioCardGroup.WithIcon = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <div className={clsx(styles.contentWithMiddledIconContainer)}>
    <div className={styles.iconContainer}>{icon}</div>
    <div className={styles.contentContainer}>{children}</div>
  </div>
);

export * from './components/RadioOptionButton';
